export const EDEN_ACTION_TYPES = {
  NAVIGATE_ROUTE: "navigate_route",
  OPEN_EXTERNAL: "open_external",
  OPEN_MODAL: "open_modal",
  SELECT_PLATFORM: "select_platform",
  SELECT_ADVERTISER: "select_advertiser",
  NOOP_UNAVAILABLE: "noop_unavailable",
  ANALYSIS_FALLBACK: "analysis_fallback",
  CLARIFY: "clarify",
};

const MUSIC_LINKS = {
  appleMusic: "https://music.apple.com",
  spotify: "https://open.spotify.com",
  pandora: "https://www.pandora.com",
  slack: "https://app.slack.com/client",
};

const action = ({ id, label, type = EDEN_ACTION_TYPES.NAVIGATE_ROUTE, route, url, aliases = [], message, unavailable = false }) => ({
  id,
  label,
  type: unavailable ? EDEN_ACTION_TYPES.NOOP_UNAVAILABLE : type,
  route: route || null,
  url: url || null,
  aliases: [label, ...aliases].filter(Boolean),
  confirmation: message || `Opening ${label}.`,
  unavailableMessage: "That action is not available for this account.",
});

export const EDEN_ACTION_REGISTRY = [
  action({ id: "dashboard", label: "Dashboard", route: "/dashboard", aliases: ["home", "cphere dashboard"] }),
  action({
    id: "sherloc-reports",
    label: "SherlocReports",
    route: "/sherloc-reports",
    aliases: ["sherloc reports", "platform intelligence", "reports", "reporting"],
  }),
  action({ id: "optimus", label: "Optimus", route: "/optimus", aliases: ["pacing", "optimus pacing"] }),
  action({
    id: "media-planner",
    label: "Sherloc Media Planner",
    route: "/sherloc-media-planner",
    aliases: ["media planner", "planner", "genesis planner", "sherloc planner"],
  }),
  action({
    id: "upcoming-campaigns",
    label: "Upcoming Campaigns",
    route: "/sherloc-media-campaigns",
    aliases: [
      "campaigns",
      "media campaigns",
      "planned campaigns",
      "campaigns list",
      "sherloc campaigns",
    ],
  }),
  action({ id: "genesis", label: "Genesis", route: "/genesis-hub", aliases: ["genesis hub"] }),
  action({
    id: "campaign-insights",
    label: "Campaign Insights",
    route: "/ai-optimizations",
    aliases: ["ai optimizations", "ai optimisation", "campaign intelligence", "intelligence snapshot"],
  }),
  action({
    id: "audience-intelligence",
    label: "Audience Intelligence",
    aliases: ["audience insights", "audience manager"],
    unavailable: true,
  }),
  action({ id: "cinema", label: "Cinema Intelligence", route: "/cinema-analytics", aliases: ["cinema analytics"] }),
  action({
    id: "platform-analytics",
    label: "Platform Analytics",
    route: "/platform-analytics",
    aliases: ["platform analytics", "platform performance", "unified analytics"],
  }),
  action({ id: "social", label: "Social Intelligence", route: "/programmatic-analytics", aliases: ["social analytics"] }),
  action({
    id: "programmatic",
    label: "Omni Analytics",
    route: "/programmatic-analytics",
    aliases: ["programmatic", "omni analytics", "omni intelligence"],
  }),
  // Creative Trafficking is a protected Beta surface (CREATIVE_BETA_INTERNAL_ACCESS).
  // It keeps an entry so Ask EDEN answers the request instead of falling through
  // to a near-miss route — but `unavailable` means it carries NO route, so the
  // intent resolves to the canonical client-safe NOOP rather than navigating.
  action({
    id: "creative-trafficking",
    label: "Creative Trafficking",
    aliases: ["trafficking"],
    unavailable: true,
  }),
  // The client-authorized creative library (delivery per creative).
  action({
    id: "creative-assets",
    label: "Creative Assets",
    route: "/creative-assets",
    aliases: ["creative assets", "creative results", "creative delivery", "creative library"],
  }),
  // "upload creatives" is a real Creative Studio capability and keeps working.
  action({
    id: "creative",
    label: "Creative Studio",
    route: "/creative-studio",
    aliases: ["studio", "creative", "upload creatives"],
  }),
  action({
    id: "manager",
    label: "Manager",
    route: "/campaign-management",
    aliases: ["campaign manager", "campaigns manager", "campaign management"],
  }),
  action({ id: "settings", label: "Settings", route: "/settings", aliases: ["account settings"] }),
  action({
    id: "pantheon",
    label: "Pantheon",
    route: "/pantheon",
    aliases: ["pantheon network", "intelligence network"],
  }),
  action({
    id: "facebook",
    label: "Facebook Analytics",
    route: "/facebook-analytics",
    aliases: ["facebook", "meta analytics", "meta", "facebook page", "facebook analytics page"],
  }),
  action({ id: "instagram", label: "Instagram Analytics", route: "/instagram-analytics", aliases: ["instagram", "ig analytics", "ig"] }),
  action({ id: "tiktok", label: "TikTok Analytics", route: "/tiktok-analytics", aliases: ["tiktok", "tik tok analytics", "tik tok"] }),
  action({ id: "youtube", label: "YouTube Analytics", route: "/youtube-analytics", aliases: ["youtube", "yt analytics", "yt"] }),
  action({ id: "x", label: "X Analytics", route: "/x-analytics", aliases: ["x", "twitter analytics", "twitter"] }),
  action({
    id: "ga4",
    label: "Google Analytics",
    route: "/google-analytics",
    aliases: ["ga4", "google analytics", "google analytics 4", "google search analytics", "google search"],
  }),
  action({ id: "audio", label: "Audio Analytics", route: "/audio-analytics", aliases: ["audio"] }),
  action({ id: "ingame", label: "In-Game Analytics", route: "/ingame-analytics", aliases: ["in game analytics", "in-game", "in game"] }),
  action({ id: "ctv", label: "CTV Analytics", route: "/ctv-analytics", aliases: ["ctv", "ott analytics", "ott"] }),
  action({ id: "video", label: "Video Analytics", route: "/video-analytics", aliases: ["video", "olv analytics", "olv"] }),
  action({ id: "mobile", label: "Mobile Analytics", route: "/mobile-analytics", aliases: ["mobile"] }),
  action({ id: "display", label: "Display Analytics", route: "/display-analytics", aliases: ["display"] }),
  action({ id: "dooh", label: "DOOH Analytics", route: "/dooh-analytics", aliases: ["dooh", "digital out of home"] }),
  action({ id: "apple-music", label: "Apple Music", type: EDEN_ACTION_TYPES.OPEN_EXTERNAL, url: MUSIC_LINKS.appleMusic, aliases: ["music"] }),
  action({ id: "spotify", label: "Spotify", type: EDEN_ACTION_TYPES.OPEN_EXTERNAL, url: MUSIC_LINKS.spotify }),
  action({ id: "pandora", label: "Pandora", type: EDEN_ACTION_TYPES.OPEN_EXTERNAL, url: MUSIC_LINKS.pandora }),
  action({ id: "slack", label: "Slack", type: EDEN_ACTION_TYPES.OPEN_EXTERNAL, url: MUSIC_LINKS.slack }),
  action({ id: "platform-ops", label: "Platform Ops", aliases: ["platform operations", "ad manager selector"], unavailable: true }),
];

const ACTION_PHRASES = [
  "take me to",
  "go to",
  "navigate to",
  "open up",
  "open",
  "launch",
  "show me",
  "show",
  "bring up",
  "pull up",
  "send me to",
  "bring me to",
  "take me",
  "go",
];

const ANALYSIS_PATTERNS = [
  /\banaly[sz]e\b/,
  /\banalysis\b/,
  /\bperformance\b/,
  /\bwhat(?:'s| is) happening\b/,
  /\bwhat is going on\b/,
  /\bwhy\b/,
  /\bdiagnose\b/,
  /\bexplain\b/,
  /\bsummarize\b/,
  /\bsummarise\b/,
  /\bbreak down\b/,
  /\bhow (?:is|are|did|does)\b/,
];

export const normalizeEdenActionText = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[-_/]+/g, " ")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const ACTION_PREFIX_PATTERN = new RegExp(
  `^(?:(?:hey\\s+)?eden\\s+)?(?:(?:can|could|would|will)\\s+you\\s+)?(?:please\\s+)?(?:${ACTION_PHRASES.join(
    "|"
  ).replace(/\s+/g, "\\s+")})\\b`
);

const hasActionIntent = (text) => ACTION_PREFIX_PATTERN.test(text);

const hasAnalysisIntent = (text) => ANALYSIS_PATTERNS.some((pattern) => pattern.test(text));

const normalizeAlias = (alias) => normalizeEdenActionText(alias);

export const resolveEdenActionIntent = (prompt = "", registry = EDEN_ACTION_REGISTRY) => {
  const normalized = normalizeEdenActionText(prompt);
  if (!normalized) return { type: EDEN_ACTION_TYPES.ANALYSIS_FALLBACK };

  if (hasAnalysisIntent(normalized)) {
    return { type: EDEN_ACTION_TYPES.ANALYSIS_FALLBACK };
  }

  const hasExplicitAction = hasActionIntent(normalized);

  if (
    hasExplicitAction &&
    /\b(?:facebook|meta)\b/.test(normalized) &&
    /\bsettings?\b/.test(normalized)
  ) {
    return {
      type: EDEN_ACTION_TYPES.CLARIFY,
      message: "Do you want Facebook Analytics or Meta platform settings?",
    };
  }

  const matches = [];
  registry.forEach((entry) => {
    entry.aliases
      .map(normalizeAlias)
      .filter(Boolean)
      .forEach((alias) => {
        const aliasPattern = new RegExp(`\\b${alias.replace(/\s+/g, "\\s+")}\\b`);
        if (
          (hasExplicitAction && aliasPattern.test(normalized)) ||
          (!hasExplicitAction && normalized === alias)
        ) {
          matches.push({ entry, aliasLength: alias.length });
        }
      });
  });

  if (!matches.length) {
    return { type: EDEN_ACTION_TYPES.ANALYSIS_FALLBACK };
  }

  matches.sort((a, b) => b.aliasLength - a.aliasLength);
  return matches[0].entry;
};
